import { useCallback, useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import useAlert from 'utils/alert';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { AccountingType } from 'v2-types/order';
import { deleteCharges, updateCharges } from 'contexts/GeneralChargesContext/query';
import { validationSchemaCharges } from 'views/orders/new-charges/services/validate-entry';
import { formatAccountingData, getAccountingLetter } from './utils';
import { ChargeFormEntry } from './types';
import { useGeneralChargesContext } from 'contexts/GeneralChargesContext';
import { ModalState } from 'contexts/GeneralChargesContext/useCharges';

export type ChargesFormType = {
	charges: ChargeFormEntry[];
};

type Params = {
	accountingData: AccountingType[];
	currentLetter?: string;
	accountingFilter?: string;
};

export const useTableGeneralCharges = ({
  accountingData,
  currentLetter,
  accountingFilter,
}: Params) => {
  const {
    modalState,
    updateModalState,
    lockedCharges,
    orderId,
    handleToggleDisclosureSection,
    openConfirmationModal,
    disclosureSection,
    handleCloseModal,
  } = useGeneralChargesContext();

  const [formatedAccountingData, setFormatedAccountingData] = useState(
    formatAccountingData(accountingData, lockedCharges) || []
  );

  const showAlert = useAlert();
  const { t } = useTranslation();

  const chargesForm = useForm<ChargesFormType>({
    defaultValues: {
      charges: formatedAccountingData,
    },
    resolver: zodResolver(validationSchemaCharges),
    context: { charges: formatedAccountingData },
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { isValid, isDirty },
  } = chargesForm;

  useEffect(() => {
    const fortmatted =
			formatAccountingData(accountingData, lockedCharges).sort(
			  (chargeA, chargeB) => chargeA.number - chargeB.number
			) || [];
    setFormatedAccountingData(fortmatted);
    reset({ charges: fortmatted });
    // reset is unstable; adding it would re-run this effect on every form render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountingData, lockedCharges]);

  const updateChargesState = (charges: ChargeFormEntry[], shouldDirtyForm?: boolean) => {
    const newCharges = charges;
    setFormatedAccountingData(newCharges);
    reset({ charges: newCharges }, { keepDirty: shouldDirtyForm ?? true });
  };

  const { mutate: setUpdateCharges, ...updateChargesResponse } = useMutation(updateCharges, {
    onSuccess: () => {
      showAlert(t('charges:success-updated'), 'success');
    },
    onError: () => {
      showAlert(t('charges:error-updated'), 'error');
    },
  });

  const { mutate: setDeleteCharges, ...deleteChargesResponse } = useMutation(deleteCharges, {
    onSuccess: (data) => {
      handleCloseModal();
      showAlert(t('charges:success-deleted'), 'success');
    },
    onError: () => {
      showAlert(t('charges:error-deleted'), 'error');
    },
  });

  const handleDeleteCharge = () => {
    if (modalState.modal !== 'deleteDocument') return;

    const { accounting_id, number } = modalState.charge;
    if (accounting_id) {
      setDeleteCharges({
        id: orderId,
        accountingId: accounting_id,
      });
    } else {
      const { charges } = getValues();
      const newData = charges.filter((charge) => charge.number !== number);
      updateChargesState(newData);
      handleCloseModal();
    }
  };

  const hanldeUpdateCharge = (dataCharges) => {
    setUpdateCharges({
      id: orderId,
      data: dataCharges,
      filters: {
        accounting: currentLetter ? getAccountingLetter(currentLetter) : accountingFilter,
      },
    } as any);
  };

  const createNewEmptyEntry = useCallback(() => {
    const emptyCharge: ChargeFormEntry = {
      code: '',
      accounting_id: null,
      description: '',
      kind: 'manual',
      number: 0,
      months: 0,
      per_month: 0,
      tax: 0,
      payees: [],
      payers: [],
      taxes: [],
      payee_id: '',
      seller_before: 0,
      seller_at: 0,
      buyer_before: 0,
      buyer_at: 0,
      by_others: 0,
      letter: currentLetter ? getAccountingLetter(currentLetter) : '',
      amount_override: 0,
      amount_calculated: 0,
      disabled: false,
      amount: 0,
    };

    return emptyCharge;
  }, [currentLetter]);

  const hanldeAddEmptyCharges = () => {
    const { charges } = getValues();
    const chargesLength = charges.length - 1;
    const getChargeEight = charges.find((f) => f.number === 8);
    const tempcharges = [...charges];

    const emptyCharge = createNewEmptyEntry();
    emptyCharge.number =
			(formatedAccountingData[chargesLength] !== undefined
			  ? formatedAccountingData[chargesLength].number
			  : 0) + 1;

    if (charges.length >= 8) {
      updateChargesState([...charges, emptyCharge]);
    } else if (getChargeEight) {
      const existingNumbers = new Set(charges.map((c) => c.number));
      let missingNumber = 1;
      // eslint-disable-next-line no-plusplus
      for (let i = 1; i <= 8; i++) {
        if (!existingNumbers.has(i)) {
          missingNumber = i;
          break;
        }
      }
      emptyCharge.number = missingNumber;

      const insertIndex = tempcharges.findIndex((c) => c.number > missingNumber);
      if (insertIndex === -1) {
        tempcharges.push(emptyCharge);
      } else {
        tempcharges.splice(insertIndex, 0, emptyCharge);
      }
      updateChargesState(tempcharges);
    } else {
      updateChargesState([...charges, emptyCharge]);
    }
  };

  const handleOpenDeleteDocumentChargeModal = (rowIndex: number) => {
    updateModalState({
      modal: 'deleteDocument',
      charge: chargesForm.getValues(`charges.${rowIndex}`),
      rowIndex,
    });
  };

  const handleOpenEditDocumentChargeModal = (charge: ChargeFormEntry, rowIndex: number) => {
    updateModalState({
      modal: 'editDocument',
      charge,
      rowIndex,
    } as ModalState);
  };

  const handleOpenSplitAmountsModal = (
    charge: ChargeFormEntry,
    kind: 'Payee' | 'Payer' | 'Tax',
    editAllCharges: boolean = false
  ) => {
    updateModalState({
      modal: 'editSplit',
      charges: editAllCharges ? formatedAccountingData : [charge],
      kind,
    } as ModalState);
  };

  const handleOpenChangeDisclosurePolicy = () => {
    openConfirmationModal({
      confirmationTitle: `Send Title Charges to ${disclosureSection === 'B' ? 'C' : 'B'}`,
      confirmationMessage: `Are you sure you want to send the title charges to letter ${disclosureSection === 'B' ? 'C' : 'B'}?`,
      onConfirm: handleToggleDisclosureSection,
    });
  };

  return {
    handleOpenEditDocumentChargeModal,
    modalState,
    handleOpenDeleteDocumentChargeModal,
    hanldeUpdateCharge,
    isLoading: updateChargesResponse.isLoading || deleteChargesResponse.isLoading,
    t,
    handleDeleteCharge,
    formatedAccountingData,
    hanldeAddEmptyCharges,
    control,
    getValues,
    handleSubmit,
    isValid,
    isFormDirty: isDirty,
    handleOpenSplitAmountsModal,
    handleOpenChangeDisclosurePolicy,
    chargesForm,
  };
};
